import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { generateInstrumental } from '../utils/MusicGenerator';
import { 
  Music, Mic, Sliders, Wand2, Play, Pause, Square, Download, Send, 
  Volume2, Loader2, Sparkles, ArrowRight, Crown, Upload,
  Headphones, Piano, Star, Zap, Check, Search, Filter, DollarSign,
  Share2, Heart, Package, Layers, Disc3, Radio
} from 'lucide-react';
import KazajahDAW from './KazajahDAW';
import AIMusicTab from './AIMusicTab';
import GwadaFlagBg from './GwadaFlagBg';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { id: 'lyrics', label: 'Paroles', icon: Mic, color: 'from-pink-500 to-rose-500', desc: 'Ecris des paroles dans tous les styles' },
  { id: 'chords', label: 'Accords', icon: Piano, color: 'from-blue-500 to-cyan-500', desc: 'Progressions et arrangements musicaux' },
  { id: 'remix', label: 'Remix', icon: Sliders, color: 'from-purple-500 to-violet-500', desc: 'Idees de remix dans tous les genres' },
  { id: 'production', label: 'Production', icon: Wand2, color: 'from-orange-500 to-amber-500', desc: 'Conseils mixage, mastering, sound design' },
];

const GENRES = [
  'Reggae', 'Hip Hop', 'House', 'Techno', 'Afrobeat', 'Amapiano', 'Zouk', 'Kompa', 
  'Elektro Ka', 'Funk', 'Soul', 'R&B', 'Jazz', 'Dancehall', 'Drum & Bass',
  'Pop', 'Rock', 'Bossa Nova', 'Trap', 'Lo-Fi', 'Gospel'
];

export default function StudioPage({ onBack }) {
  const [activeTab, setActiveTab] = useState('daw');
  const [chatMode, setChatMode] = useState('lyrics');
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `studio-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  // When AI Music tab fires "send-to-daw", switch to DAW tab automatically
  useEffect(() => {
    const onSendToDaw = () => setActiveTab('daw');
    window.addEventListener('kazajah:send-to-daw', onSendToDaw);
    return () => window.removeEventListener('kazajah:send-to-daw', onSendToDaw);
  }, []);

  const [ttsText, setTtsText] = useState('');
  const [ttsVoice, setTtsVoice] = useState('alloy');
  const [ttsSpeed, setTtsSpeed] = useState(1.0);
  const [ttsModel, setTtsModel] = useState('tts-1');
  const [voices, setVoices] = useState([]);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const chatEndRef = useRef(null);
  const [plans, setPlans] = useState([]);
  const [samples, setSamples] = useState([]);
  const [sampleFilter, setSampleFilter] = useState('all');
  const [sampleSearch, setSampleSearch] = useState('');

  useEffect(() => {
    fetch(`${API}/studio/voices`).then(r => r.json()).then(setVoices).catch(() => {});
    fetch(`${API}/studio/plans`).then(r => r.json()).then(setPlans).catch(() => {});
    fetch(`${API}/studio/samples`).then(r => r.json()).then(setSamples).catch(() => {});
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = useCallback(async () => {
    if (!inputText.trim() || loading) return;
    const userMsg = inputText.trim();
    setInputText('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg, mode: chatMode }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/studio/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg, session_id: sessionId, mode: chatMode })
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'ai', text: data.response, mode: chatMode }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: "Erreur de connexion. Reessayez.", mode: chatMode }]);
    }
    setLoading(false);
  }, [inputText, loading, chatMode, sessionId]);

  const generateVoice = async () => {
    if (!ttsText.trim() || ttsLoading) return;
    setTtsLoading(true); setAudioUrl(null);
    try {
      const res = await fetch(`${API}/studio/tts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: ttsText, voice: ttsVoice, speed: ttsSpeed, model: ttsModel })
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      setAudioUrl(URL.createObjectURL(blob));
    } catch { alert("Erreur lors de la generation vocale"); }
    setTtsLoading(false);
  };

  const handleSubscribe = async (planId) => {
    try {
      const res = await fetch(`${API}/studio/subscribe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId, origin_url: window.location.origin })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch { alert("Erreur lors de la souscription"); }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) audioRef.current.pause(); else audioRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  // Music generation state
  const [musicPrompt, setMusicPrompt] = useState('');
  const [musicStyle, setMusicStyle] = useState('Reggae');
  const [musicLang, setMusicLang] = useState('francais');
  const [musicVoice, setMusicVoice] = useState('nova');
  const [musicLoading, setMusicLoading] = useState(false);
  const [generatedSong, setGeneratedSong] = useState(null);
  const [songAudioUrl, setSongAudioUrl] = useState(null);
  const [songPlaying, setSongPlaying] = useState(false);
  const songAudioRef = useRef(null);
  const [genProgress, setGenProgress] = useState({ pct: 0, msg: '' });
  const [genMode, setGenMode] = useState('full'); // 'full' = lyrics+vocals+beat, 'instrumental' = beat only

  // Generate REAL instrumental beat
  const generateBeat = async () => {
    setMusicLoading(true);
    setGeneratedSong(null);
    setSongAudioUrl(null);
    setGenProgress({ pct: 0, msg: 'Demarrage...' });
    try {
      const result = await generateInstrumental(musicStyle, 8, (pct, msg) => {
        setGenProgress({ pct, msg });
      });
      setSongAudioUrl(result.url);
      setGeneratedSong({
        style: musicStyle,
        voice: null,
        lyrics: null,
        type: 'instrumental',
        bpm: result.bpm,
        key: result.key,
        scale: result.scale,
        blob: result.blob
      });
    } catch (err) {
      console.error('Beat generation error:', err);
      alert("Erreur lors de la generation. Reessayez.");
    }
    setMusicLoading(false);
  };

  // Generate full song: beat + lyrics + vocals
  const generateMusic = async () => {
    if (!musicPrompt.trim() || musicLoading) return;
    setMusicLoading(true);
    setGeneratedSong(null);
    setSongAudioUrl(null);
    setGenProgress({ pct: 0, msg: 'Demarrage...' });
    try {
      // Step 1: Generate beat
      setGenProgress({ pct: 10, msg: 'Generation du beat instrumental...' });
      const beatResult = await generateInstrumental(musicStyle, 8, (pct, msg) => {
        setGenProgress({ pct: Math.floor(pct * 0.4), msg });
      });

      // Step 2: Generate lyrics via AI
      setGenProgress({ pct: 45, msg: 'Ecriture des paroles par l\'IA...' });
      const lyricsRes = await fetch(`${API}/studio/generate-music`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: musicPrompt, voice: musicVoice, style: musicStyle, language: musicLang, model: 'tts-1-hd', speed: 1.0 })
      });
      if (!lyricsRes.ok) throw new Error('Lyrics generation failed');
      const lyricsData = await lyricsRes.json();

      setGenProgress({ pct: 80, msg: 'Mixage beat + voix...' });

      // Step 3: Mix beat + vocals using Web Audio
      const vocalsBytes = Uint8Array.from(atob(lyricsData.audio_base64), c => c.charCodeAt(0));
      const vocalsBlob = new Blob([vocalsBytes], { type: 'audio/mpeg' });

      // Mix the two audio sources
      const mixedUrl = await mixAudioSources(beatResult.blob, vocalsBlob);

      setGenProgress({ pct: 100, msg: 'Chanson prete !' });

      setSongAudioUrl(mixedUrl);
      setGeneratedSong({
        style: musicStyle,
        voice: musicVoice,
        lyrics: lyricsData.lyrics,
        audio_base64: lyricsData.audio_base64,
        type: 'full',
        bpm: beatResult.bpm,
        key: beatResult.key,
        scale: beatResult.scale
      });
    } catch (err) {
      console.error('Music generation error:', err);
      alert("Erreur lors de la generation. Reessayez.");
    }
    setMusicLoading(false);
  };

  // Mix two audio blobs using Web Audio API
  const mixAudioSources = async (beatBlob, vocalsBlob) => {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    try {
      const [beatBuf, vocalsBuf] = await Promise.all([
        beatBlob.arrayBuffer().then(ab => audioCtx.decodeAudioData(ab)),
        vocalsBlob.arrayBuffer().then(ab => audioCtx.decodeAudioData(ab))
      ]);

      const maxLen = Math.max(beatBuf.length, vocalsBuf.length);
      const offCtx = new OfflineAudioContext(2, maxLen, beatBuf.sampleRate);

      const beatSrc = offCtx.createBufferSource();
      beatSrc.buffer = beatBuf;
      const beatGain = offCtx.createGain();
      beatGain.gain.value = 0.7;
      beatSrc.connect(beatGain);
      beatGain.connect(offCtx.destination);
      beatSrc.start(0);

      const vocalsSrc = offCtx.createBufferSource();
      vocalsSrc.buffer = vocalsBuf;
      const vocalsGain = offCtx.createGain();
      vocalsGain.gain.value = 1.0;
      vocalsSrc.connect(vocalsGain);
      vocalsGain.connect(offCtx.destination);
      vocalsSrc.start(0);

      const mixed = await offCtx.startRendering();

      // Encode to WAV
      const wavBlob = audioBufferToWavBlob(mixed);
      audioCtx.close();
      return URL.createObjectURL(wavBlob);
    } catch {
      // Fallback: just return the beat if mixing fails
      audioCtx.close();
      return URL.createObjectURL(beatBlob);
    }
  };

  const audioBufferToWavBlob = (buffer) => {
    const numCh = buffer.numberOfChannels;
    const sr = buffer.sampleRate;
    const samples = buffer.length;
    const dataSize = samples * numCh * 2;
    const ab = new ArrayBuffer(44 + dataSize);
    const v = new DataView(ab);
    const w = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
    w(0, 'RIFF'); v.setUint32(4, 36 + dataSize, true); w(8, 'WAVE'); w(12, 'fmt ');
    v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, numCh, true);
    v.setUint32(24, sr, true); v.setUint32(28, sr * numCh * 2, true);
    v.setUint16(32, numCh * 2, true); v.setUint16(34, 16, true); w(36, 'data'); v.setUint32(40, dataSize, true);
    const chs = []; for (let c = 0; c < numCh; c++) chs.push(buffer.getChannelData(c));
    let off = 44;
    for (let i = 0; i < samples; i++) {
      for (let c = 0; c < numCh; c++) {
        const s = Math.max(-1, Math.min(1, chs[c][i]));
        v.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        off += 2;
      }
    }
    return new Blob([ab], { type: 'audio/wav' });
  };

  const toggleSongPlay = () => {
    if (songAudioRef.current) {
      if (songPlaying) songAudioRef.current.pause(); else songAudioRef.current.play();
      setSongPlaying(!songPlaying);
    }
  };

  const downloadSong = () => {
    if (songAudioUrl) {
      const a = document.createElement('a');
      a.href = songAudioUrl;
      a.download = `kazajah_${musicStyle.toLowerCase()}_${musicVoice}.mp3`;
      a.click();
    }
  };

  const TABS = [
    { id: 'daw', label: 'Studio Pro DAW', icon: Sliders },
    { id: 'ai-music', label: 'AI Music', icon: Music },
    { id: 'ai-chat', label: 'Texte & Paroles', icon: Sparkles },
    { id: 'plans', label: 'Plans', icon: Crown }
  ];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="studio-page">
      {/* Hero header */}
      <section className="relative py-8 px-4 md:px-8 bg-gradient-to-b from-emerald-950/60 via-black to-black overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full blur-[120px]" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-500 rounded-full blur-[150px]" />
        </div>
        <div className="relative max-w-6xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button onClick={onBack} data-testid="studio-back-button" className="text-zinc-400 hover:text-white transition-colors mb-3 inline-flex items-center gap-2 text-sm">
              <ArrowRight className="rotate-180" size={16} /> Retour
            </button>
            <div className="flex items-center justify-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                <Sparkles size={18} className="text-black" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-black">
                <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">KAZAJAH AI</span>
              </h1>
            </div>
            <p className="text-xs text-zinc-500">DAW Pro Unifie • Stems • Beat Maker • AI Voice • AI Music • Master • Lyrics</p>
          </motion.div>
        </div>
      </section>

      {/* DAW only — all sub-tools live inside the DAW topbar */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4">
        <KazajahDAW />
      </div>

      {/* Hidden offscreen helpers (Lyrics chat + AI Music + Voice) for any cross-component event still using activeTab.
          Note: KazajahDAW already has its own LYRICS, AI MUSIC, VOICE, BEATS, STEMS, MASTER panels in its topbar. */}
    </div>
  );

  // legacy multi-tab body kept below for reference (unreachable)
  // eslint-disable-next-line no-unreachable
  return (
    <div className="min-h-screen bg-black text-white" data-testid="studio-page-legacy">
      {/* Hero */}
      <section className="relative py-12 px-4 md:px-8 bg-gradient-to-b from-emerald-950/60 via-black to-black overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full blur-[120px]" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-500 rounded-full blur-[150px]" />
        </div>
        <div className="relative max-w-6xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button onClick={onBack} data-testid="studio-back-button" className="text-zinc-400 hover:text-white transition-colors mb-4 inline-flex items-center gap-2 text-sm">
              <ArrowRight className="rotate-180" size={16} /> Retour
            </button>
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                <Sparkles size={20} className="text-black" />
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black">
                <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">KAZAJAH AI</span>
              </h1>
            </div>
            <p className="text-sm text-zinc-400 max-w-xl mx-auto">Texte & Paroles • Voice AI • Stems Separator • DAW • Monetisation</p>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-0 z-40 bg-black/90 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-6xl mx-auto flex overflow-x-auto px-4 scrollbar-hide">
          {TABS.map(tab => (
            <button key={tab.id} data-testid={`studio-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${
                activeTab === tab.id ? 'border-emerald-400 text-emerald-400' : 'border-transparent text-zinc-500 hover:text-white'
              }`}>
              <tab.icon size={14} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">

          {/* AI CHAT */}
          {activeTab === 'ai-chat' && (
            <motion.div key="ai-chat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                {MODES.map(mode => (
                  <button key={mode.id} data-testid={`mode-${mode.id}-button`} onClick={() => setChatMode(mode.id)}
                    className={`p-3 rounded-xl border transition-all text-left ${chatMode === mode.id ? `bg-gradient-to-br ${mode.color} border-transparent text-white` : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-600'}`}>
                    <mode.icon size={18} className="mb-1" />
                    <p className="font-bold text-xs">{mode.label}</p>
                    <p className="text-[10px] opacity-70">{mode.desc}</p>
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {GENRES.slice(0, 10).map(g => (
                  <button key={g} onClick={() => setInputText(`Ecris-moi quelque chose en style ${g}`)}
                    className="px-2.5 py-1 rounded-full bg-zinc-800/60 text-zinc-400 text-[11px] hover:bg-zinc-700 hover:text-white transition-all border border-zinc-700/50">{g}</button>
                ))}
              </div>
              <div className="bg-zinc-900/60 rounded-2xl border border-zinc-800 overflow-hidden" style={{ minHeight: '350px' }}>
                <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
                  {messages.length === 0 && (
                    <div className="text-center py-12">
                      <Sparkles size={32} className="mx-auto mb-3 text-emerald-400" />
                      <p className="text-zinc-500 font-semibold">KAZAJAH AI Studio</p>
                      <p className="text-zinc-600 text-xs mt-1">Paroles, accords, remix, production...</p>
                      <div className="flex flex-wrap gap-1.5 justify-center mt-4">
                        {["Ecris un refrain reggae", "Accords pour un zouk", "Remix house en Elektro Ka", "Tips pour mixer un beat trap"].map((s, i) => (
                          <button key={i} onClick={() => setInputText(s)} className="px-2.5 py-1.5 rounded-lg bg-zinc-800 text-zinc-400 text-[11px] hover:bg-zinc-700 hover:text-white transition-all">{s}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {messages.map((msg, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white' : 'bg-zinc-800 text-zinc-200 border border-zinc-700'}`}>
                        {msg.role === 'ai' && <div className="flex items-center gap-1 mb-1 text-emerald-400 text-[10px] font-semibold"><Sparkles size={10} /> KAZAJAH AI • {MODES.find(m => m.id === msg.mode)?.label}</div>}
                        <div className="whitespace-pre-wrap text-xs leading-relaxed">{msg.text}</div>
                        {msg.role === 'ai' && (
                          <button onClick={() => { setTtsText(msg.text.slice(0, 4096)); setActiveTab('voice'); }}
                            className="mt-2 flex items-center gap-1 text-[10px] text-emerald-400 hover:text-emerald-300 transition-colors"><Mic size={10} /> Voix AI</button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                  {loading && <div className="flex justify-start"><div className="bg-zinc-800 border border-zinc-700 p-3 rounded-2xl"><Loader2 size={18} className="animate-spin text-emerald-400" /></div></div>}
                  <div ref={chatEndRef} />
                </div>
                <div className="p-3 border-t border-zinc-800 bg-zinc-900/80 flex gap-2">
                  <input data-testid="studio-chat-input" value={inputText} onChange={e => setInputText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    placeholder={`Mode: ${MODES.find(m => m.id === chatMode)?.label}...`}
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-white text-sm placeholder-zinc-500 focus:border-emerald-500 focus:outline-none" />
                  <button data-testid="studio-chat-send" onClick={sendMessage} disabled={loading || !inputText.trim()}
                    className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-bold rounded-xl hover:from-emerald-400 hover:to-teal-400 disabled:opacity-40"><Send size={16} /></button>
                </div>
              </div>
            </motion.div>
          )}

          {/* VOICE */}
          {activeTab === 'ai-music' && (
            <motion.div key="ai-music" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <AIMusicTab />
            </motion.div>
          )}

          {activeTab === 'voice' && (
            <motion.div key="voice" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <textarea data-testid="tts-text-input" value={ttsText} onChange={e => setTtsText(e.target.value)} rows={6}
                    placeholder="Entrez les paroles ou le texte..." className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-3 text-white text-sm placeholder-zinc-500 focus:border-emerald-500 focus:outline-none resize-none" />
                  <p className="text-[10px] text-zinc-600">{ttsText.length}/4096</p>
                  <div className="flex gap-2">
                    {[{ id: 'tts-1', label: 'Standard' }, { id: 'tts-1-hd', label: 'HD' }].map(m => (
                      <button key={m.id} onClick={() => setTtsModel(m.id)} data-testid={`tts-model-${m.id}`}
                        className={`flex-1 p-2.5 rounded-xl border text-center text-xs font-bold ${ttsModel === m.id ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'bg-zinc-900 border-zinc-700 text-zinc-400'}`}>{m.label}</button>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Vitesse: {ttsSpeed.toFixed(1)}x</label>
                    <input type="range" min="0.5" max="2.0" step="0.1" value={ttsSpeed} onChange={e => setTtsSpeed(parseFloat(e.target.value))} className="w-full accent-emerald-500" />
                  </div>
                  <button data-testid="generate-voice-button" onClick={generateVoice} disabled={ttsLoading || !ttsText.trim()}
                    className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-black font-bold rounded-xl disabled:opacity-40 flex items-center justify-center gap-2 text-sm">
                    {ttsLoading ? <><Loader2 size={16} className="animate-spin" /> Generation...</> : <><Mic size={16} /> Generer la voix</>}
                  </button>
                </div>
                <div className="space-y-4">
                  <label className="text-xs font-semibold text-zinc-400 block">Voix</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {voices.map(v => (
                      <button key={v.id} data-testid={`voice-${v.id}`} onClick={() => setTtsVoice(v.id)}
                        className={`p-2 rounded-lg border text-center ${ttsVoice === v.id ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}>
                        <p className="font-bold text-xs">{v.name}</p>
                        <p className="text-[9px] opacity-60">{v.description}</p>
                      </button>
                    ))}
                  </div>
                  {audioUrl && (
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30">
                      <audio ref={audioRef} src={audioUrl} onEnded={() => setIsPlaying(false)} />
                      <div className="flex items-center gap-3">
                        <button data-testid="play-generated-audio" onClick={togglePlay} className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center text-black">
                          {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
                        </button>
                        <div className="flex-1">
                          <p className="text-white font-semibold text-sm">KAZAJAH Voice AI</p>
                          <p className="text-zinc-400 text-xs">Voix: {voices.find(v => v.id === ttsVoice)?.name}</p>
                        </div>
                        <button data-testid="download-audio" onClick={() => { const a = document.createElement('a'); a.href = audioUrl; a.download = `kazajah_voice_${ttsVoice}.mp3`; a.click(); }}
                          className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-emerald-400"><Download size={16} /></button>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* SAMPLES & STEMS */}
          {activeTab === 'samples' && (
            <motion.div key="samples" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <SamplesLibrary samples={samples} filter={sampleFilter} setFilter={setSampleFilter} search={sampleSearch} setSearch={setSampleSearch} />
            </motion.div>
          )}

          {/* STEMS SEPARATOR */}
          {activeTab === 'stems' && (
            <motion.div key="stems" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <StemsSeparator />
            </motion.div>
          )}

          {/* DAW Unified */}
          {activeTab === 'daw' && (
            <motion.div key="daw" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <KazajahDAW />
            </motion.div>
          )}

          {/* MONETIZE */}
          {activeTab === 'monetize' && (
            <motion.div key="monetize" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <MonetizePage />
            </motion.div>
          )}

          {/* PLANS */}
          {activeTab === 'plans' && (
            <motion.div key="plans" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-black text-white">Choisissez votre <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Plan</span></h2>
                <p className="text-zinc-400 text-sm mt-1">Debloquez tout le potentiel de KAZAJAH STUDIO</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {plans.map((plan, i) => (
                  <motion.div key={plan.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                    className={`p-5 rounded-2xl border ${plan.id === 'pro' ? 'bg-gradient-to-b from-emerald-500/10 to-cyan-500/10 border-emerald-500/50 scale-105' : 'bg-zinc-900/50 border-zinc-800'}`}>
                    {plan.id === 'pro' && <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-500 text-black text-[10px] font-bold mb-2">Populaire</span>}
                    <h3 className="text-xl font-black text-white">{plan.name}</h3>
                    <div className="mt-2 mb-4"><span className="text-3xl font-black text-white">{plan.price === 0 ? 'Gratuit' : `${plan.price}€`}</span>{plan.price > 0 && <span className="text-zinc-500 text-xs">/mois</span>}</div>
                    <ul className="space-y-2 mb-4">
                      {plan.features.map((f, j) => <li key={j} className="flex items-center gap-2 text-xs text-zinc-300"><Check size={14} className="text-emerald-400 flex-shrink-0" /> {f}</li>)}
                    </ul>
                    <button data-testid={`subscribe-${plan.id}`} onClick={() => plan.price > 0 ? handleSubscribe(plan.id) : null}
                      className={`w-full py-2.5 rounded-xl font-bold text-sm ${plan.id === 'pro' ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-black' : plan.price === 0 ? 'bg-zinc-800 text-zinc-400 cursor-default' : 'bg-zinc-800 text-white hover:bg-zinc-700'}`}>
                      {plan.price === 0 ? 'Plan actuel' : 'Souscrire'}
                    </button>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ========= SAMPLES & STEMS LIBRARY =========
function SamplesLibrary({ samples, filter, setFilter, search, setSearch }) {
  const categories = ['all', 'drums', 'bass', 'synth', 'vocals', 'loops', 'stems', 'fx'];
  const filtered = samples.filter(s => {
    if (filter !== 'all' && s.category !== filter) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase()) && !s.tags.some(t => t.includes(search.toLowerCase()))) return false;
    return true;
  });

  return (
    <div data-testid="samples-library">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-black text-white flex items-center gap-2"><Layers size={20} className="text-emerald-400" /> Samples & Stems</h2>
          <p className="text-zinc-500 text-xs mt-0.5">Bibliotheque style Splice — Glisse dans le DAW pour remixer</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="sample-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
            className="pl-8 pr-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-xs focus:border-emerald-500 focus:outline-none w-48" />
        </div>
      </div>

      <div className="flex gap-1.5 mb-4 overflow-x-auto">
        {categories.map(cat => (
          <button key={cat} data-testid={`filter-${cat}`} onClick={() => setFilter(cat)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
              filter === cat ? 'bg-emerald-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}>
            {cat === 'all' ? `Tous (${samples.length})` : cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((pack, i) => (
          <motion.div key={pack.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 hover:border-emerald-500/40 transition-all group">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-sm font-bold text-white">{pack.name}</h3>
                <p className="text-[10px] text-zinc-500">{pack.artist}</p>
              </div>
              {pack.category === 'stems' ? (
                <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 text-[10px] font-bold">STEMS</span>
              ) : (
                <span className="px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 text-[10px] font-bold">{pack.category.toUpperCase()}</span>
              )}
            </div>
            <div className="flex items-center gap-3 text-[10px] text-zinc-500 mb-3">
              {pack.bpm > 0 && <span>{pack.bpm} BPM</span>}
              {pack.key !== 'N/A' && <span>Key: {pack.key}</span>}
              <span>{pack.samples} samples</span>
            </div>
            <div className="flex flex-wrap gap-1 mb-3">
              {pack.tags.map(tag => (
                <span key={tag} className="px-1.5 py-0.5 rounded bg-zinc-800/80 text-zinc-500 text-[9px]">#{tag}</span>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className={`font-bold text-sm ${pack.price === 0 ? 'text-emerald-400' : 'text-yellow-400'}`}>
                {pack.price === 0 ? 'GRATUIT' : `${pack.price.toFixed(2)}€`}
              </span>
              <div className="flex gap-1.5">
                <button className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-all" title="Previsualiser">
                  <Play size={14} />
                </button>
                <button data-testid={`add-to-daw-${pack.id}`}
                  className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500 hover:text-black text-[10px] font-bold transition-all flex items-center gap-1">
                  <Download size={12} /> {pack.price === 0 ? 'Ajouter au DAW' : 'Acheter'}
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ========= STEMS SEPARATOR =========
function StemsSeparator() {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [stems, setStems] = useState(null);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);
  const audioCtxRef = useRef(null);

  const separateStems = async () => {
    if (!file) return;
    setProcessing(true);
    setStems(null);
    setProgress(10);

    try {
      const arrayBuf = await file.arrayBuffer();
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      setProgress(20);

      const audioBuffer = await ctx.decodeAudioData(arrayBuf);
      const sr = audioBuffer.sampleRate;
      const len = audioBuffer.length;
      const channels = audioBuffer.numberOfChannels;
      setProgress(30);

      // Create filtered versions using OfflineAudioContext
      const createFiltered = async (low, high, label) => {
        const offCtx = new OfflineAudioContext(channels, len, sr);
        const src = offCtx.createBufferSource();
        src.buffer = audioBuffer;

        if (low > 0) {
          const hp = offCtx.createBiquadFilter();
          hp.type = 'highpass'; hp.frequency.value = low; hp.Q.value = 0.7;
          src.connect(hp);
          if (high < 20000) {
            const lp = offCtx.createBiquadFilter();
            lp.type = 'lowpass'; lp.frequency.value = high; lp.Q.value = 0.7;
            hp.connect(lp); lp.connect(offCtx.destination);
          } else {
            hp.connect(offCtx.destination);
          }
        } else {
          const lp = offCtx.createBiquadFilter();
          lp.type = 'lowpass'; lp.frequency.value = high; lp.Q.value = 0.7;
          src.connect(lp); lp.connect(offCtx.destination);
        }

        src.start(0);
        const rendered = await offCtx.startRendering();

        // Convert to WAV blob
        const dataSize = rendered.length * channels * 2;
        const ab = new ArrayBuffer(44 + dataSize);
        const v = new DataView(ab);
        const w = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
        w(0, 'RIFF'); v.setUint32(4, 36 + dataSize, true); w(8, 'WAVE'); w(12, 'fmt ');
        v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, channels, true);
        v.setUint32(24, sr, true); v.setUint32(28, sr * channels * 2, true);
        v.setUint16(32, channels * 2, true); v.setUint16(34, 16, true); w(36, 'data'); v.setUint32(40, dataSize, true);
        const chs = []; for (let c = 0; c < channels; c++) chs.push(rendered.getChannelData(c));
        let off = 44;
        for (let i = 0; i < rendered.length; i++) {
          for (let c = 0; c < channels; c++) {
            const s = Math.max(-1, Math.min(1, chs[c][i]));
            v.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
            off += 2;
          }
        }
        const blob = new Blob([ab], { type: 'audio/wav' });
        return { label, blob, url: URL.createObjectURL(blob), buffer: rendered };
      };

      setProgress(40);
      const bass = await createFiltered(0, 250, 'Bass');
      setProgress(55);
      const drums = await createFiltered(250, 5000, 'Drums & Mid');
      setProgress(70);
      const vocals = await createFiltered(300, 4000, 'Vocals (Mid)');
      setProgress(85);
      const highs = await createFiltered(5000, 20000, 'Highs & Air');
      setProgress(95);

      ctx.close();
      setStems([
        { ...bass, color: 'from-red-500 to-orange-500', icon: '🎸' },
        { ...drums, color: 'from-blue-500 to-cyan-500', icon: '🥁' },
        { ...vocals, color: 'from-emerald-500 to-teal-500', icon: '🎤' },
        { ...highs, color: 'from-purple-500 to-pink-500', icon: '✨' }
      ]);
      setProgress(100);
    } catch (err) {
      console.error('Stem separation error:', err);
      alert("Erreur lors de la separation. Verifiez le format du fichier.");
    }
    setProcessing(false);
  };

  const StemPlayer = ({ stem }) => {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);

    return (
      <div className={`p-4 rounded-xl border border-zinc-800 bg-gradient-to-r ${stem.color} bg-opacity-5`}
        style={{ background: `linear-gradient(135deg, rgba(0,0,0,0.8), rgba(0,0,0,0.95))` }}>
        <div className="flex items-center gap-3">
          <button onClick={() => {
            if (playing) { audioRef.current?.pause(); setPlaying(false); }
            else { audioRef.current?.play(); setPlaying(true); }
          }}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              playing ? `bg-gradient-to-r ${stem.color} text-black` : 'bg-zinc-800 text-white hover:bg-zinc-700'
            }`}>
            {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
          </button>
          <audio ref={audioRef} src={stem.url} onEnded={() => setPlaying(false)} />
          <div className="flex-1">
            <p className="text-white font-bold text-sm">{stem.icon} {stem.label}</p>
          </div>
          <a href={stem.url} download={`stem_${stem.label.toLowerCase().replace(/[^a-z]/g, '_')}.wav`}
            className="px-3 py-1.5 bg-zinc-800 text-zinc-400 rounded-lg text-xs hover:bg-zinc-700 flex items-center gap-1">
            <Download size={12} /> WAV
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="stems-separator">
      <div className="text-center">
        <h2 className="text-2xl font-black text-white flex items-center justify-center gap-2">
          <Layers size={24} className="text-cyan-400" /> Stems Separator
        </h2>
        <p className="text-zinc-400 text-sm mt-1">Uploadez un track et separéz-le en stems (basse, drums, vocals, highs)</p>
      </div>

      {/* Upload zone */}
      <div className="relative">
        <input ref={fileRef} type="file" accept=".mp3,.wav,.ogg,.flac,.m4a,.aac"
          onChange={e => { setFile(e.target.files?.[0] || null); setStems(null); }}
          data-testid="stems-file-input"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
        <div className={`p-8 rounded-2xl border-2 border-dashed text-center transition-all ${
          file ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-zinc-700 bg-zinc-900/30 hover:border-zinc-500'
        }`}>
          {file ? (
            <div>
              <Music size={32} className="text-cyan-400 mx-auto mb-2" />
              <p className="text-white font-bold">{file.name}</p>
              <p className="text-zinc-400 text-xs mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          ) : (
            <div>
              <Upload size={32} className="text-zinc-500 mx-auto mb-2" />
              <p className="text-zinc-400 font-semibold">Glissez un fichier audio ici ou cliquez</p>
              <p className="text-zinc-600 text-xs mt-1">MP3, WAV, OGG, FLAC, M4A</p>
            </div>
          )}
        </div>
      </div>

      {file && !stems && (
        <button onClick={separateStems} disabled={processing} data-testid="separate-stems-button"
          className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-black font-black rounded-xl disabled:opacity-40 flex items-center justify-center gap-2 text-sm">
          {processing ? (
            <><Loader2 size={18} className="animate-spin" /> Separation en cours... {progress}%</>
          ) : (
            <><Layers size={18} /> Separer en Stems</>
          )}
        </button>
      )}

      {processing && (
        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}

      {/* Stems Results */}
      {stems && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Layers size={18} className="text-cyan-400" /> 4 Stems extraits
          </h3>
          {stems.map((stem, i) => <StemPlayer key={i} stem={stem} />)}
          <button onClick={() => { setFile(null); setStems(null); if (fileRef.current) fileRef.current.value = ''; }}
            className="w-full py-2.5 bg-zinc-800 text-zinc-400 rounded-xl hover:bg-zinc-700 text-sm font-semibold">
            Nouveau fichier
          </button>
        </motion.div>
      )}

      <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
        <p className="text-cyan-400 text-xs font-semibold">La separation est basee sur des filtres audio frequentiels. Pour une separation ML professionnelle (type Spleeter/Demucs), un service externe sera bientot disponible.</p>
      </div>
    </div>
  );
}


// ========= MINI DAW WITH BEAT MAKER & STEMS =========
function MiniDAW({ samples }) {
  const INSTRUMENTS = ['Kick', 'Snare', 'HiHat', 'Clap', 'Tom', 'Rim', 'Shaker', 'OpenHH'];
  const STEPS = 16;
  const [bpm, setBpm] = useState(120);
  const [masterKey, setMasterKey] = useState(0); // semitones offset
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'stems'
  const [pattern, setPattern] = useState(() => {
    const p = {};
    INSTRUMENTS.forEach(inst => { p[inst] = new Array(STEPS).fill(false); });
    p['Kick'] = [true,false,false,false, true,false,false,false, true,false,false,false, true,false,false,false];
    p['Snare'] = [false,false,false,false, true,false,false,false, false,false,false,false, true,false,false,false];
    p['HiHat'] = [true,false,true,false, true,false,true,false, true,false,true,false, true,false,true,false];
    return p;
  });
  const [volumes, setVolumes] = useState(() => {
    const v = {};
    INSTRUMENTS.forEach(inst => { v[inst] = 80; });
    return v;
  });
  const [pitches, setPitches] = useState(() => {
    const p = {};
    INSTRUMENTS.forEach(inst => { p[inst] = 0; }); // semitones -12 to +12
    return p;
  });
  const [muted, setMuted] = useState(() => {
    const m = {};
    INSTRUMENTS.forEach(inst => { m[inst] = false; });
    return m;
  });
  const [solo, setSolo] = useState(null);
  const [showSampleBrowser, setShowSampleBrowser] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCode, setAdminCode] = useState('');
  const audioCtxRef = useRef(null);
  const intervalRef = useRef(null);
  const stepRef = useRef(-1);

  const getAudioCtx = () => {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return audioCtxRef.current;
  };

  const playSound = useCallback((instrument, vol = 80, pitchOffset = 0) => {
    const ctx = getAudioCtx();
    const gain = ctx.createGain();
    gain.gain.value = vol / 100 * 0.5;
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    const pitchMult = Math.pow(2, pitchOffset / 12);

    if (instrument === 'Kick') {
      const osc = ctx.createOscillator(); osc.type = 'sine';
      osc.frequency.setValueAtTime(160 * pitchMult, now); osc.frequency.exponentialRampToValueAtTime(30 * pitchMult, now + 0.15);
      gain.gain.setValueAtTime(vol / 100 * 0.5, now); gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      const sub = ctx.createOscillator(); sub.type = 'sine';
      sub.frequency.setValueAtTime(55 * pitchMult, now); sub.frequency.exponentialRampToValueAtTime(28 * pitchMult, now + 0.3);
      const subG = ctx.createGain(); subG.gain.setValueAtTime(vol / 100 * 0.5, now); subG.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      sub.connect(subG); subG.connect(ctx.destination); sub.start(now); sub.stop(now + 0.4);
      osc.connect(gain); osc.start(now); osc.stop(now + 0.3);
    } else if (instrument === 'Snare') {
      const noise = ctx.createBufferSource(); const buf = ctx.createBuffer(1, ctx.sampleRate * 0.12, ctx.sampleRate); const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 1.8);
      noise.buffer = buf;
      const filt = ctx.createBiquadFilter(); filt.type = 'highpass'; filt.frequency.value = 1500 * pitchMult;
      noise.connect(filt); filt.connect(gain); noise.start(now);
      const osc = ctx.createOscillator(); osc.type = 'triangle'; osc.frequency.setValueAtTime(250 * pitchMult, now);
      osc.frequency.exponentialRampToValueAtTime(100 * pitchMult, now + 0.06);
      const g2 = ctx.createGain(); g2.gain.setValueAtTime(vol / 100 * 0.4, now); g2.gain.exponentialRampToValueAtTime(0.01, now + 0.08);
      osc.connect(g2); g2.connect(ctx.destination); osc.start(now); osc.stop(now + 0.1);
    } else if (instrument === 'HiHat') {
      const noise = ctx.createBufferSource(); const buf = ctx.createBuffer(1, ctx.sampleRate * 0.04, ctx.sampleRate); const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 5);
      noise.buffer = buf;
      const filt = ctx.createBiquadFilter(); filt.type = 'highpass'; filt.frequency.value = 7000 * pitchMult;
      gain.gain.setValueAtTime(vol / 100 * 0.3, now); gain.gain.exponentialRampToValueAtTime(0.01, now + 0.04);
      noise.connect(filt); filt.connect(gain); noise.start(now);
    } else if (instrument === 'OpenHH') {
      const noise = ctx.createBufferSource(); const buf = ctx.createBuffer(1, ctx.sampleRate * 0.2, ctx.sampleRate); const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 1.5);
      noise.buffer = buf;
      const filt = ctx.createBiquadFilter(); filt.type = 'highpass'; filt.frequency.value = 6000 * pitchMult;
      gain.gain.setValueAtTime(vol / 100 * 0.25, now); gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
      noise.connect(filt); filt.connect(gain); noise.start(now);
    } else if (instrument === 'Clap') {
      for (let j = 0; j < 3; j++) {
        const noise = ctx.createBufferSource(); const buf = ctx.createBuffer(1, ctx.sampleRate * 0.02, ctx.sampleRate); const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1; noise.buffer = buf;
        const filt = ctx.createBiquadFilter(); filt.type = 'bandpass'; filt.frequency.value = 2000 * pitchMult;
        const g = ctx.createGain(); g.gain.setValueAtTime(vol / 100 * 0.3, now + j * 0.015); g.gain.exponentialRampToValueAtTime(0.01, now + j * 0.015 + 0.04);
        noise.connect(filt); filt.connect(g); g.connect(ctx.destination); noise.start(now + j * 0.015);
      }
    } else if (instrument === 'Tom') {
      const osc = ctx.createOscillator(); osc.type = 'sine'; osc.frequency.setValueAtTime(200 * pitchMult, now);
      osc.frequency.exponentialRampToValueAtTime(60 * pitchMult, now + 0.2);
      gain.gain.setValueAtTime(vol / 100 * 0.4, now); gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
      osc.connect(gain); osc.start(now); osc.stop(now + 0.25);
    } else if (instrument === 'Rim') {
      const osc = ctx.createOscillator(); osc.type = 'square'; osc.frequency.setValueAtTime(800 * pitchMult, now);
      gain.gain.setValueAtTime(vol / 100 * 0.2, now); gain.gain.exponentialRampToValueAtTime(0.01, now + 0.03);
      osc.connect(gain); osc.start(now); osc.stop(now + 0.03);
    } else if (instrument === 'Shaker') {
      const noise = ctx.createBufferSource(); const buf = ctx.createBuffer(1, ctx.sampleRate * 0.06, ctx.sampleRate); const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1; noise.buffer = buf;
      const filt = ctx.createBiquadFilter(); filt.type = 'bandpass'; filt.frequency.value = 8000 * pitchMult; filt.Q.value = 2;
      gain.gain.setValueAtTime(vol / 100 * 0.15, now); gain.gain.exponentialRampToValueAtTime(0.01, now + 0.06);
      noise.connect(filt); filt.connect(gain); noise.start(now);
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      clearInterval(intervalRef.current);
      setIsPlaying(false);
      setCurrentStep(-1);
      stepRef.current = -1;
    } else {
      getAudioCtx();
      setIsPlaying(true);
      stepRef.current = -1;
      const stepTime = (60000 / bpm) / 4;
      intervalRef.current = setInterval(() => {
        stepRef.current = (stepRef.current + 1) % STEPS;
        setCurrentStep(stepRef.current);
      }, stepTime);
    }
  }, [isPlaying, bpm]);

  useEffect(() => {
    if (currentStep >= 0 && isPlaying) {
      INSTRUMENTS.forEach(inst => {
        const canPlay = solo ? (inst === solo) : !muted[inst];
        if (pattern[inst][currentStep] && canPlay) {
          playSound(inst, volumes[inst], pitches[inst] + masterKey);
        }
      });
    }
  }, [currentStep, isPlaying, pattern, muted, solo, volumes, pitches, masterKey, playSound]);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const toggleCell = (inst, step) => {
    setPattern(prev => {
      const next = { ...prev, [inst]: [...prev[inst]] };
      next[inst][step] = !next[inst][step];
      return next;
    });
  };

  const clearPattern = () => {
    setPattern(() => {
      const next = {};
      INSTRUMENTS.forEach(inst => { next[inst] = new Array(STEPS).fill(false); });
      return next;
    });
  };

  const randomize = () => {
    setPattern(() => {
      const next = {};
      INSTRUMENTS.forEach(inst => {
        next[inst] = new Array(STEPS).fill(false).map(() => Math.random() > 0.75);
      });
      next['Kick'][0] = true; next['Kick'][8] = true;
      next['Snare'][4] = true; next['Snare'][12] = true;
      return next;
    });
  };

  const presets = {
    'Boom Bap': () => { const p = {}; INSTRUMENTS.forEach(i => p[i] = new Array(16).fill(false)); p['Kick']=[true,false,false,false,false,false,false,false,true,false,true,false,false,false,false,false]; p['Snare']=[false,false,false,false,true,false,false,false,false,false,false,false,true,false,false,false]; p['HiHat']=[true,true,true,true,true,true,true,true,true,true,true,true,true,true,true,true]; setPattern(p); setBpm(90); },
    'House': () => { const p = {}; INSTRUMENTS.forEach(i => p[i] = new Array(16).fill(false)); p['Kick']=[true,false,false,false,true,false,false,false,true,false,false,false,true,false,false,false]; p['Clap']=[false,false,false,false,true,false,false,false,false,false,false,false,true,false,false,false]; p['HiHat']=[false,false,true,false,false,false,true,false,false,false,true,false,false,false,true,false]; p['OpenHH']=[true,false,false,false,false,false,false,false,true,false,false,false,false,false,false,false]; setPattern(p); setBpm(124); },
    'Reggae': () => { const p = {}; INSTRUMENTS.forEach(i => p[i] = new Array(16).fill(false)); p['Kick']=[false,false,false,false,false,false,true,false,false,false,false,false,false,false,true,false]; p['Snare']=[false,false,false,false,true,false,false,false,false,false,false,false,true,false,false,false]; p['HiHat']=[true,false,true,false,true,false,true,false,true,false,true,false,true,false,true,false]; p['Rim']=[true,false,false,true,false,false,true,false,true,false,false,true,false,false,true,false]; setPattern(p); setBpm(80); },
    'Trap': () => { const p = {}; INSTRUMENTS.forEach(i => p[i] = new Array(16).fill(false)); p['Kick']=[true,false,false,false,false,false,true,false,false,false,true,false,false,false,false,false]; p['Snare']=[false,false,false,false,true,false,false,false,false,false,false,false,true,false,false,false]; p['HiHat']=[true,true,true,true,true,true,true,true,true,true,true,true,true,true,true,true]; p['Clap']=[false,false,false,false,true,false,false,false,false,false,false,false,true,false,false,true]; setPattern(p); setBpm(140); },
    'Elektro Ka': () => { const p = {}; INSTRUMENTS.forEach(i => p[i] = new Array(16).fill(false)); p['Kick']=[true,false,false,true,false,false,true,false,true,false,false,true,false,false,true,false]; p['Tom']=[false,false,true,false,false,true,false,false,false,false,true,false,false,true,false,false]; p['Shaker']=[true,true,true,true,true,true,true,true,true,true,true,true,true,true,true,true]; p['Clap']=[false,false,false,false,true,false,false,true,false,false,false,false,true,false,false,true]; setPattern(p); setBpm(128); },
    'Afrobeat': () => { const p = {}; INSTRUMENTS.forEach(i => p[i] = new Array(16).fill(false)); p['Kick']=[true,false,false,true,false,false,true,false,false,false,true,false,false,true,false,false]; p['Snare']=[false,false,false,false,true,false,false,false,false,false,false,false,true,false,false,true]; p['HiHat']=[true,true,false,true,true,false,true,true,false,true,true,false,true,true,false,true]; p['Shaker']=[false,true,true,false,false,true,false,true,false,true,true,false,false,true,false,true]; setPattern(p); setBpm(108); },
  };

  const handleAdminLogin = () => {
    if (adminCode === 'kazajah2024') { setIsAdmin(true); setAdminCode(''); }
  };

  const allSamples = isAdmin ? (samples || []) : (samples || []).filter(s => !s.premium);
  const instColors = { Kick: 'bg-red-500', Snare: 'bg-orange-500', HiHat: 'bg-yellow-500', Clap: 'bg-pink-500', Tom: 'bg-blue-500', Rim: 'bg-cyan-500', Shaker: 'bg-green-500', OpenHH: 'bg-purple-500' };
  const KEYS = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];

  return (
    <div className="space-y-4" data-testid="mini-daw">
      {/* Admin toggle */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-black text-white flex items-center gap-2"><Sliders size={18} className="text-emerald-400" /> Beat Maker & DAW</h2>
        {!isAdmin ? (
          <div className="flex items-center gap-2">
            <input type="password" value={adminCode} onChange={e => setAdminCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAdminLogin()}
              placeholder="Code admin" className="w-28 bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1 text-white text-[10px] focus:border-emerald-500 focus:outline-none" />
            <button onClick={handleAdminLogin} className="px-2 py-1 bg-zinc-800 text-zinc-400 rounded-lg text-[10px] hover:bg-zinc-700">Admin</button>
          </div>
        ) : (
          <span className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 text-[10px] font-bold flex items-center gap-1"><Crown size={12} /> ADMIN</span>
        )}
      </div>

      {/* Transport + Master Controls */}
      <div className="flex items-center gap-3 p-3 bg-zinc-900 rounded-xl border border-zinc-800 flex-wrap">
        <button data-testid="daw-play" onClick={togglePlay} className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isPlaying ? 'bg-emerald-500 text-black' : 'bg-zinc-700 text-white hover:bg-zinc-600'}`}>
          {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
        </button>
        <button data-testid="daw-stop" onClick={() => { clearInterval(intervalRef.current); setIsPlaying(false); setCurrentStep(-1); stepRef.current = -1; }}
          className="w-10 h-10 rounded-full bg-zinc-700 text-white hover:bg-zinc-600 flex items-center justify-center"><Square size={14} /></button>
        
        <div className="flex items-center gap-1.5">
          <span className="text-zinc-500 text-xs font-semibold">BPM:</span>
          <input data-testid="daw-bpm" type="number" value={bpm} onChange={e => setBpm(Math.max(40, Math.min(300, parseInt(e.target.value) || 120)))}
            className="w-14 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-white text-center text-xs" />
        </div>

        {/* Master Tonality / Pitch */}
        <div className="flex items-center gap-1.5 border-l border-zinc-700 pl-3">
          <span className="text-zinc-500 text-xs font-semibold">Tonalite:</span>
          <button onClick={() => setMasterKey(p => Math.max(-12, p - 1))} className="w-6 h-6 rounded bg-zinc-800 text-zinc-400 hover:bg-zinc-700 flex items-center justify-center text-xs font-bold">-</button>
          <span data-testid="master-key" className="text-white text-xs font-bold w-8 text-center">{masterKey > 0 ? `+${masterKey}` : masterKey}</span>
          <button onClick={() => setMasterKey(p => Math.min(12, p + 1))} className="w-6 h-6 rounded bg-zinc-800 text-zinc-400 hover:bg-zinc-700 flex items-center justify-center text-xs font-bold">+</button>
          <span className="text-zinc-600 text-[10px]">({KEYS[(masterKey % 12 + 12) % 12]})</span>
        </div>

        <div className="flex gap-1.5 ml-2">
          <button onClick={clearPattern} className="px-2 py-1 bg-zinc-800 text-zinc-400 rounded text-[10px] hover:bg-zinc-700">Clear</button>
          <button onClick={randomize} className="px-2 py-1 bg-zinc-800 text-zinc-400 rounded text-[10px] hover:bg-zinc-700">Random</button>
        </div>
        <div className="flex-1" />

        {/* View toggle */}
        <div className="flex gap-1">
          <button data-testid="view-grid" onClick={() => setViewMode('grid')}
            className={`px-2 py-1 rounded text-[10px] font-bold ${viewMode === 'grid' ? 'bg-emerald-500 text-black' : 'bg-zinc-700 text-zinc-400'}`}>Grille</button>
          <button data-testid="view-stems" onClick={() => setViewMode('stems')}
            className={`px-2 py-1 rounded text-[10px] font-bold ${viewMode === 'stems' ? 'bg-emerald-500 text-black' : 'bg-zinc-700 text-zinc-400'}`}>Stems</button>
        </div>
        <button onClick={() => setShowSampleBrowser(!showSampleBrowser)} data-testid="toggle-sample-browser"
          className={`px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1 transition-all ${showSampleBrowser ? 'bg-emerald-500 text-black' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}>
          <Package size={14} /> Samples
        </button>
      </div>

      {/* Presets */}
      <div className="flex gap-1.5 overflow-x-auto">
        <span className="text-zinc-500 text-xs font-semibold py-1">Presets:</span>
        {Object.keys(presets).map(name => (
          <button key={name} onClick={presets[name]} className="px-3 py-1 rounded-full bg-zinc-800 text-zinc-400 text-xs hover:bg-emerald-500 hover:text-black transition-all whitespace-nowrap">{name}</button>
        ))}
      </div>

      <div className="flex gap-4">
        {/* Beat Grid / Stems View */}
        <div className={`${showSampleBrowser ? 'flex-1' : 'w-full'} overflow-x-auto`}>
          {viewMode === 'grid' ? (
            <div className="min-w-[600px]">
              {/* Step numbers */}
              <div className="flex items-center gap-0.5 mb-1 pl-40">
                {Array.from({ length: STEPS }, (_, i) => (
                  <div key={i} className={`flex-1 text-center text-[9px] font-mono ${i === currentStep ? 'text-emerald-400 font-bold' : i % 4 === 0 ? 'text-zinc-500' : 'text-zinc-700'}`}>{i + 1}</div>
                ))}
              </div>
              {/* Instruments with pitch */}
              {INSTRUMENTS.map(inst => (
                <div key={inst} className="flex items-center gap-0.5 mb-0.5">
                  <div className="w-16 flex items-center gap-1">
                    <button onClick={() => playSound(inst, volumes[inst], pitches[inst] + masterKey)} className={`w-2 h-7 rounded-sm ${instColors[inst]}`} title={`Preview ${inst}`} />
                    <span className="text-[10px] text-zinc-400 font-semibold truncate">{inst}</span>
                  </div>
                  <button onClick={() => setMuted(prev => ({ ...prev, [inst]: !prev[inst] }))}
                    className={`px-1 py-0.5 text-[8px] rounded font-bold ${muted[inst] ? 'bg-red-500/30 text-red-400' : 'bg-zinc-800 text-zinc-600'}`}>M</button>
                  <button onClick={() => setSolo(solo === inst ? null : inst)}
                    className={`px-1 py-0.5 text-[8px] rounded font-bold ${solo === inst ? 'bg-yellow-500/30 text-yellow-400' : 'bg-zinc-800 text-zinc-600'}`}>S</button>
                  {/* Volume */}
                  <input type="range" min="0" max="100" value={volumes[inst]} onChange={e => setVolumes(prev => ({ ...prev, [inst]: parseInt(e.target.value) }))}
                    className="w-8 accent-emerald-500 h-0.5" title="Volume" />
                  {/* Pitch */}
                  <div className="flex items-center gap-0.5 mx-0.5" title="Pitch (semitones)">
                    <button onClick={() => setPitches(p => ({...p, [inst]: Math.max(-12, p[inst]-1)}))} className="w-3.5 h-5 rounded-sm bg-zinc-800 text-zinc-500 text-[8px] hover:bg-zinc-700 flex items-center justify-center">-</button>
                    <span className={`text-[8px] w-5 text-center font-bold ${pitches[inst] !== 0 ? 'text-cyan-400' : 'text-zinc-600'}`}>{pitches[inst] > 0 ? `+${pitches[inst]}` : pitches[inst]}</span>
                    <button onClick={() => setPitches(p => ({...p, [inst]: Math.min(12, p[inst]+1)}))} className="w-3.5 h-5 rounded-sm bg-zinc-800 text-zinc-500 text-[8px] hover:bg-zinc-700 flex items-center justify-center">+</button>
                  </div>
                  {/* Grid cells */}
                  {Array.from({ length: STEPS }, (_, step) => (
                    <button key={step} data-testid={`cell-${inst}-${step}`} onClick={() => toggleCell(inst, step)}
                      className={`flex-1 h-7 rounded-sm border transition-all ${
                        pattern[inst][step]
                          ? `${instColors[inst]} border-transparent ${step === currentStep && isPlaying ? 'brightness-150 scale-110' : ''}`
                          : `bg-zinc-900 ${step === currentStep && isPlaying ? 'border-emerald-500/50' : step % 4 === 0 ? 'border-zinc-700' : 'border-zinc-800'} hover:bg-zinc-800`
                      }`} />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            /* STEMS VIEW */
            <div className="min-w-[600px] space-y-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-bold text-white">Vue Stems — Ajuster Tempo & Tonalite par instrument</h3>
              </div>
              {INSTRUMENTS.map(inst => {
                const activeSteps = pattern[inst].filter(Boolean).length;
                return (
                  <div key={inst} className={`p-3 rounded-xl border transition-all ${
                    solo === inst ? 'bg-yellow-500/10 border-yellow-500/40' : muted[inst] ? 'bg-zinc-900/30 border-zinc-800 opacity-40' : 'bg-zinc-900/60 border-zinc-800'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-12 rounded-sm ${instColors[inst]}`} />
                      <div className="w-20">
                        <p className="text-sm font-bold text-white">{inst}</p>
                        <p className="text-[9px] text-zinc-500">{activeSteps} hits</p>
                      </div>
                      
                      {/* Mute / Solo */}
                      <div className="flex gap-1">
                        <button onClick={() => setMuted(p => ({...p, [inst]: !p[inst]}))}
                          className={`px-2 py-1 rounded text-[10px] font-bold ${muted[inst] ? 'bg-red-500 text-black' : 'bg-zinc-800 text-zinc-400'}`}>M</button>
                        <button onClick={() => setSolo(solo === inst ? null : inst)}
                          className={`px-2 py-1 rounded text-[10px] font-bold ${solo === inst ? 'bg-yellow-500 text-black' : 'bg-zinc-800 text-zinc-400'}`}>S</button>
                      </div>

                      {/* Volume slider */}
                      <div className="flex items-center gap-1.5 flex-1">
                        <Volume2 size={10} className="text-zinc-500" />
                        <input type="range" min="0" max="100" value={volumes[inst]}
                          onChange={e => setVolumes(p => ({...p, [inst]: parseInt(e.target.value)}))}
                          className="flex-1 accent-emerald-500 h-1" />
                        <span className="text-[10px] text-zinc-500 w-6 text-right">{volumes[inst]}</span>
                      </div>

                      {/* Pitch control */}
                      <div className="flex items-center gap-1.5 border-l border-zinc-700 pl-3">
                        <span className="text-[10px] text-zinc-500">Pitch:</span>
                        <button onClick={() => setPitches(p => ({...p, [inst]: Math.max(-12, p[inst]-1)}))}
                          className="w-5 h-5 rounded bg-zinc-800 text-zinc-400 text-xs hover:bg-zinc-700 flex items-center justify-center">-</button>
                        <span className={`text-xs font-bold w-8 text-center ${pitches[inst] !== 0 ? 'text-cyan-400' : 'text-zinc-500'}`}>
                          {pitches[inst] > 0 ? `+${pitches[inst]}` : pitches[inst]}
                        </span>
                        <button onClick={() => setPitches(p => ({...p, [inst]: Math.min(12, p[inst]+1)}))}
                          className="w-5 h-5 rounded bg-zinc-800 text-zinc-400 text-xs hover:bg-zinc-700 flex items-center justify-center">+</button>
                        <span className="text-[9px] text-zinc-600">st</span>
                      </div>

                      <button onClick={() => playSound(inst, volumes[inst], pitches[inst] + masterKey)}
                        className={`w-8 h-8 rounded-full ${instColors[inst]} bg-opacity-30 flex items-center justify-center hover:bg-opacity-50`}>
                        <Play size={12} className="text-white ml-0.5" />
                      </button>
                    </div>

                    {/* Visual waveform-like pattern bar */}
                    <div className="flex gap-[2px] mt-2">
                      {pattern[inst].map((active, i) => (
                        <div key={i}
                          onClick={() => toggleCell(inst, i)}
                          className={`flex-1 rounded-sm cursor-pointer transition-all ${
                            active ? `${instColors[inst]} ${i === currentStep && isPlaying ? 'h-6 brightness-150' : 'h-4'}` : `bg-zinc-800 h-2 hover:h-3 hover:bg-zinc-700`
                          }`} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sample Browser */}
        {showSampleBrowser && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="w-64 bg-zinc-900/80 rounded-xl border border-zinc-800 p-3 max-h-[500px] overflow-y-auto">
            <h3 className="text-xs font-bold text-white mb-2 flex items-center gap-1"><Package size={12} className="text-emerald-400" /> Samples</h3>
            <div className="space-y-1.5">
              {allSamples.map(pack => (
                <div key={pack.id} className="p-2 rounded-lg bg-zinc-800/60 border border-zinc-700 hover:border-emerald-500/50 transition-all">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-white">{pack.name}</p>
                    {pack.premium && !isAdmin ? (
                      <span className="text-yellow-400 text-[8px] font-bold">PRO</span>
                    ) : (
                      <span className="text-emerald-400 text-[8px] font-bold">{isAdmin && pack.premium ? 'ADMIN' : 'FREE'}</span>
                    )}
                  </div>
                  <p className="text-[9px] text-zinc-500">{pack.bpm > 0 ? `${pack.bpm} BPM • ` : ''}{pack.samples} samples</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
        <p className="text-emerald-400 text-xs font-semibold flex items-center gap-1"><Zap size={14} /> Utilisez la vue <strong>Stems</strong> pour ajuster volume et tonalite de chaque instrument. Changez le <strong>BPM</strong> et la <strong>tonalite master</strong> en haut.</p>
      </div>
    </div>
  );
}

// ========= MONETIZE PAGE =========
function MonetizePage() {
  const [formData, setFormData] = useState({ title: '', artist_name: '', genre: '', description: '', bpm: 120, key: 'N/A', email: '', paypal_email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/studio/submit-track`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) setSubmitted(true);
    } catch { alert("Erreur lors de la soumission"); }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-16">
        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4"><Check size={32} className="text-emerald-400" /></div>
        <h2 className="text-2xl font-black text-white mb-2">Morceau soumis !</h2>
        <p className="text-zinc-400 text-sm max-w-md mx-auto">Notre equipe revise votre soumission sous 48h. Revenue split: <span className="text-emerald-400 font-bold">70% pour vous</span>, 30% KAZAJAH STUDIO.</p>
        <button onClick={() => { setSubmitted(false); setFormData({ title: '', artist_name: '', genre: '', description: '', bpm: 120, key: 'N/A', email: '' }); }}
          className="mt-6 px-6 py-2 bg-zinc-800 text-white rounded-xl hover:bg-zinc-700 text-sm">Soumettre un autre morceau</button>
      </motion.div>
    );
  }

  return (
    <div data-testid="monetize-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Info */}
        <div>
          <h2 className="text-2xl font-black text-white flex items-center gap-2"><DollarSign size={24} className="text-emerald-400" /> Monetisez votre musique</h2>
          <p className="text-zinc-400 text-sm mt-2 mb-6">Publiez vos morceaux sur KAZAJAH et gagnez de l'argent via PayPal. Les paiements sont verses directement sur votre compte PayPal.</p>
          
          <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M19.5 8.5h-3.5c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h1c2.2 0 4 1.8 4 4v.5c0 1.1-.9 2-2 2z" fill="#003087"/><path d="M16.5 14.5h-3.5c-1.1 0-2-.9-2-2V10c0-1.1.9-2 2-2h1c2.2 0 4 1.8 4 4v.5c0 1.1-.9 2-2 2z" fill="#0070BA"/><path d="M3 20l1.5-9.5h4c2.5 0 4.5 2 4.5 4.5S11 19.5 8.5 19.5H6.5L6 22H3z" fill="#003087"/></svg>
              <p className="text-blue-400 font-bold text-sm">Paiements via PayPal</p>
            </div>
            <p className="text-zinc-400 text-xs">Les ventes de vos tracks sont versees sur votre compte PayPal. Revenue split: <span className="text-emerald-400 font-bold">70% pour vous</span>, 30% KAZAJAH.</p>
          </div>
          
          <div className="space-y-3">
            {[
              { icon: DollarSign, title: "Revenue Split 70/30", desc: "70% pour l'artiste, 30% pour KAZAJAH STUDIO", color: "text-emerald-400" },
              { icon: Share2, title: "Distribution exclusive", desc: "Votre musique uniquement sur kazajahstudio.com", color: "text-cyan-400" },
              { icon: Disc3, title: "Streams & Downloads", desc: "Gagnez sur chaque ecoute et telechargement", color: "text-purple-400" },
              { icon: Star, title: "Promotion par KAZAJAH", desc: "Mise en avant sur nos reseaux et playlists", color: "text-yellow-400" },
              { icon: Layers, title: "Vos Stems & Samples", desc: "Vendez vos stems et sample packs aux producteurs", color: "text-pink-400" },
              { icon: Heart, title: "Communaute KAZAJAH", desc: "Rejoignez une communaute d'artistes passionnes", color: "text-red-400" }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
                className="flex items-start gap-3 p-3 rounded-xl bg-zinc-900/60 border border-zinc-800">
                <item.icon size={18} className={`${item.color} flex-shrink-0 mt-0.5`} />
                <div>
                  <p className="text-sm font-bold text-white">{item.title}</p>
                  <p className="text-xs text-zinc-500">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div>
          <h3 className="text-lg font-bold text-white mb-4">Soumettre un morceau</h3>
          <form onSubmit={handleSubmit} className="space-y-3">
            <input data-testid="track-title" required value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
              placeholder="Titre du morceau *" className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-white text-sm placeholder-zinc-500 focus:border-emerald-500 focus:outline-none" />
            <input data-testid="track-artist" required value={formData.artist_name} onChange={e => setFormData(p => ({ ...p, artist_name: e.target.value }))}
              placeholder="Nom d'artiste *" className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-white text-sm placeholder-zinc-500 focus:border-emerald-500 focus:outline-none" />
            <input data-testid="track-email" required type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
              placeholder="Email de contact *" className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-white text-sm placeholder-zinc-500 focus:border-emerald-500 focus:outline-none" />
            <input data-testid="track-paypal" required type="email" value={formData.paypal_email} onChange={e => setFormData(p => ({ ...p, paypal_email: e.target.value }))}
              placeholder="Email PayPal (pour les paiements) *" className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-white text-sm placeholder-zinc-500 focus:border-blue-500 focus:outline-none" />
            <select data-testid="track-genre" required value={formData.genre} onChange={e => setFormData(p => ({ ...p, genre: e.target.value }))}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-white text-sm focus:border-emerald-500 focus:outline-none">
              <option value="">Genre *</option>
              {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <div className="flex gap-2">
              <input type="number" value={formData.bpm} onChange={e => setFormData(p => ({ ...p, bpm: parseInt(e.target.value) || 120 }))}
                placeholder="BPM" className="w-24 bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-white text-sm focus:border-emerald-500 focus:outline-none" />
              <input value={formData.key} onChange={e => setFormData(p => ({ ...p, key: e.target.value }))}
                placeholder="Tonalite" className="w-24 bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-white text-sm focus:border-emerald-500 focus:outline-none" />
            </div>
            <textarea data-testid="track-description" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              rows={3} placeholder="Description du morceau..." className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-white text-sm placeholder-zinc-500 focus:border-emerald-500 focus:outline-none resize-none" />
            <button data-testid="submit-track-button" type="submit" disabled={submitting}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-black font-bold rounded-xl disabled:opacity-40 flex items-center justify-center gap-2 text-sm">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Envoi...</> : <><Upload size={16} /> Soumettre mon morceau</>}
            </button>
          </form>
          <p className="text-[10px] text-zinc-600 mt-2 text-center">En soumettant, vous acceptez les conditions de KAZAJAH STUDIO. Revenue split 70/30.</p>
        </div>
      </div>
    </div>
  );
}
